import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import NewsletterSubscribe from "@/components/NewsletterSubscribe";
import {
  ArrowLeft,
  ArrowRight,
  Calendar,
  MapPin,
  Ticket,
  ExternalLink,
  Facebook,
  Instagram,
  Twitter,
  Globe,
  Music2,
  Radio,
  Plus,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * /family — public roster of independent bands DRR spins.
 *
 * For each in-rotation band from /api/family/roster:
 *  - Brutalist card with band name, genre, country, blurb
 *  - Social links row (Facebook / Instagram / X / Website / Other)
 *  - Next 3 upcoming approved gigs (venue + city + date + tickets link)
 *  - "STREAM ON DRR" chip → home
 *  - "SUBMIT A GIG" CTA on empty-gig cards → /submit-gig
 *
 * Design intent: bands feel like family, not vendors. The layout is
 * deliberately generous — one big card per band, gigs surfaced up-front.
 */
export default function Family() {
  const [bands, setBands] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/family/roster`);
        if (cancelled) return;
        setBands(Array.isArray(res?.data?.bands) ? res.data.bands : []);
      } catch (e) {
        if (!cancelled) setBands([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetch();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div
      className="min-h-screen bg-black text-zinc-50 selection:bg-red-600 selection:text-white"
      data-testid="family-page"
    >
      {/* Slim brand strip */}
      <div className="border-b border-zinc-800 bg-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-3 flex items-center justify-between gap-3 flex-wrap">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs tracking-[0.35em] uppercase text-zinc-400 hover:text-red-500 transition-colors font-display"
            data-testid="family-back-home"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.5} />
            back_to_the_signal
          </Link>
          <div className="text-[10px] tracking-[0.4em] text-zinc-500 uppercase font-display flex items-center gap-2">
            <span className="on-air-dot scale-75" aria-hidden />
            //_OUR_FAMILY
          </div>
        </div>
      </div>

      {/* Hero */}
      <section
        className="relative border-b border-zinc-800 overflow-hidden"
        data-testid="family-hero"
      >
        <div
          aria-hidden
          className="absolute inset-0 opacity-[0.06] pointer-events-none"
          style={{
            backgroundImage:
              "repeating-linear-gradient(-45deg, transparent 0 14px, rgba(220,38,38,1) 14px 16px)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-4 md:px-8 pt-14 md:pt-20 pb-10 md:pb-14">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className="text-[10px] md:text-xs tracking-[0.4em] text-red-500 uppercase font-display">
              //_OUR_FAMILY
            </span>
            <span className="h-px w-16 bg-red-600 hidden sm:inline-block" />
            <span className="text-[10px] md:text-xs tracking-[0.4em] text-zinc-500 uppercase font-display">
              INDEPENDENT ARTISTS IN ROTATION
            </span>
          </div>
          <h1 className="font-display text-[10vw] md:text-[6vw] lg:text-[5vw] leading-[0.9] tracking-tighter text-zinc-50">
            THE BANDS WE <span className="text-red-600">BLEED FOR</span>.
          </h1>
          <p className="mt-6 max-w-3xl text-base md:text-lg text-zinc-300 leading-snug">
            Every artist here is on rotation at DRR. They earned it. Now they
            own a slice of this station. Their upcoming shows, their socials,
            their whole story — right here, alongside ours.
          </p>
          <div className="mt-6 flex items-center gap-3 flex-wrap">
            <Link
              to="/submit-gig"
              className="inline-flex items-center gap-2 border-2 border-red-600 bg-red-600 text-white hover:bg-white hover:text-black px-5 py-3 text-xs md:text-sm tracking-widest uppercase font-display transition-colors"
              data-testid="family-submit-gig-cta"
            >
              <Plus className="w-4 h-4" strokeWidth={2.5} />
              BAND? POST YOUR NEXT SHOW
            </Link>
            <Link
              to="/submit"
              className="inline-flex items-center gap-2 border border-zinc-700 hover:border-red-600 px-5 py-3 text-xs md:text-sm tracking-widest uppercase font-display text-zinc-100 hover:text-red-500 transition-colors"
              data-testid="family-submit-music-cta"
            >
              SUBMIT YOUR MUSIC <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>


      {/* Roster */}
      <section className="border-b border-zinc-800 bg-black">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          {loading ? (
            <div className="text-center py-12 text-zinc-500 tracking-widest text-xs uppercase font-display">
              //_pulling_the_family_tree…
            </div>
          ) : !bands?.length ? (
            <div
              className="border border-dashed border-zinc-700 p-8 md:p-12 text-center"
              data-testid="family-empty"
            >
              <div className="font-display text-2xl md:text-3xl text-zinc-50 tracking-tight">
                THE ROSTER IS <span className="text-red-600">LOADING</span>.
              </div>
              <p className="mt-3 text-sm text-zinc-400 max-w-md mx-auto">
                DRR is currently onboarding independents. Bands, submit your
                music and once you&apos;re in rotation you&apos;ll land here.
              </p>
              <Link
                to="/submit"
                className="mt-6 inline-flex items-center gap-2 border-2 border-red-600 bg-red-600 text-white hover:bg-white hover:text-black px-5 py-3 text-xs tracking-widest uppercase font-display transition-colors"
              >
                SUBMIT YOUR MUSIC <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-zinc-800">
              {bands.map((b, i) => (
                <BandCard key={b.id} band={b} index={i} total={bands.length} />
              ))}
            </div>
          )}
        </div>
      </section>

      <section
        className="border-b border-zinc-800 bg-zinc-950"
        data-testid="family-newsletter"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-14 md:py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
            <div>
              <div className="text-xs tracking-[0.4em] text-red-500 uppercase mb-3 font-display flex items-center gap-2">
                <span className="on-air-dot scale-75" aria-hidden />
                //_WEEKLY_DIGEST
              </div>
              <h2 className="font-display text-3xl md:text-5xl leading-tight tracking-tight text-zinc-50">
                THIS WEEK ON <span className="text-red-600">THE ROAD</span>.
              </h2>
              <p className="mt-4 text-sm md:text-base text-zinc-300 leading-relaxed">
                Every Monday morning we drop a rundown of every show every DRR
                band is playing in the next 7 days — venues, cities, tickets.
                One email a week, zero fluff, all rock.
              </p>
            </div>
            <div>
              <NewsletterSubscribe source="family" />
              <p className="mt-3 text-[10px] text-zinc-500 tracking-widest uppercase font-display">
                //_unsubscribe_anytime · we_never_share_your_email
              </p>
            </div>
          </div>
        </div>
      </section>

      <footer className="border-t border-zinc-800 bg-zinc-950">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6 flex items-center justify-between flex-wrap gap-3 text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-display">
          <span>© {new Date().getFullYear()} digital revolution radio</span>
          <span>//_your_stage_our_signal</span>
          <Link
            to="/"
            className="hover:text-red-500 transition-colors"
            data-testid="family-footer-home"
          >
            ← RETURN TO STATION
          </Link>
        </div>
      </footer>
    </div>
  );
}

function BandCard({ band, index, total }) {
  const socials = [
    { key: "social_facebook", label: "Facebook", icon: Facebook, url: band.social_facebook },
    { key: "social_instagram", label: "Instagram", icon: Instagram, url: band.social_instagram },
    { key: "social_twitter", label: "X / Twitter", icon: Twitter, url: band.social_twitter },
    { key: "social_website", label: "Website", icon: Globe, url: band.social_website },
    { key: "social_other", label: "Other", icon: ExternalLink, url: band.social_other },
  ].filter((s) => (s.url || "").trim());

  const gigs = Array.isArray(band.upcoming_gigs) ? band.upcoming_gigs : [];
  const isRightCol = index % 2 === 1;
  const isLastRow = index >= total - (total % 2 === 0 ? 2 : 1);

  return (
    <div
      className={`p-6 md:p-8 bg-zinc-950 flex flex-col gap-5 min-w-0 ${
        !isRightCol ? "md:border-r border-zinc-800" : ""
      } ${!isLastRow ? "border-b border-zinc-800" : ""}`}
      data-testid={`family-band-card-${band.id}`}
    >
      {/* Head */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-2xl md:text-4xl leading-tight tracking-tight text-zinc-50 break-words">
            {band.band_name}
          </h3>
          <div className="mt-1 text-xs md:text-sm text-red-500 tracking-wide font-mono uppercase">
            {band.genre}
            {band.country ? ` · ${band.country}` : ""}
          </div>
        </div>
        <a
          href="/"
          className="shrink-0 inline-flex items-center gap-1.5 border border-red-600 bg-red-600/10 text-red-400 hover:bg-red-600 hover:text-white px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-display transition-colors"
          data-testid={`family-band-tune-in-${band.id}`}
        >
          <Radio className="w-3 h-3" strokeWidth={2.5} />
          IN ROTATION
        </a>
      </div>

      {/* Feature track */}
      {band.song_title && (
        <div className="flex items-center gap-2 text-sm text-zinc-300">
          <Music2 className="w-4 h-4 text-zinc-500 shrink-0" strokeWidth={2} />
          <span className="truncate" title={band.song_title}>
            <span className="text-zinc-500 mr-1">//_spinning:</span>
            {band.song_title}
          </span>
        </div>
      )}

      {/* Blurb */}
      {band.message && (
        <p className="text-sm text-zinc-400 leading-relaxed line-clamp-4">
          {band.message}
        </p>
      )}

      {/* Socials */}
      {socials.length > 0 && (
        <div className="flex items-center gap-2 flex-wrap">
          {socials.map((s) => (
            <a
              key={s.key}
              href={s.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 border border-zinc-700 hover:border-red-600 px-2.5 py-1.5 text-[10px] tracking-widest uppercase font-display text-zinc-300 hover:text-red-500 transition-colors"
              title={s.label}
              data-testid={`family-band-social-${s.key}-${band.id}`}
            >
              <s.icon className="w-3 h-3" strokeWidth={2} />
              {s.label}
            </a>
          ))}
        </div>
      )}

      {/* Gigs */}
      <div className="border-t border-zinc-800 pt-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[10px] tracking-[0.35em] text-red-500 uppercase font-display flex items-center gap-1.5">
            <Calendar className="w-3 h-3" strokeWidth={2.5} />
            NEXT SHOWS
          </div>
          {band.gig_count > 3 && (
            <span className="text-[10px] tracking-[0.25em] uppercase font-display text-zinc-500">
              +{band.gig_count - 3} more
            </span>
          )}
        </div>
        {gigs.length === 0 ? (
          <div className="text-xs text-zinc-500 italic">
            No upcoming shows posted yet.{" "}
            <Link
              to="/submit-gig"
              className="text-red-400 hover:text-red-500 not-italic uppercase tracking-widest font-display text-[10px]"
              data-testid={`family-band-post-gig-${band.id}`}
            >
              Post one →
            </Link>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {gigs.map((g) => (
              <GigRow key={g.id} gig={g} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function GigRow({ gig }) {
  const location = [gig.city, gig.state_country].filter(Boolean).join(", ");
  const dateStr = formatGigDate(gig.gig_date);
  return (
    <div
      className="flex items-center justify-between gap-3 border border-zinc-800 hover:border-red-600 bg-black px-3 py-2 transition-colors"
      data-testid={`family-gig-row-${gig.id}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        <div className="shrink-0 text-center font-display border border-red-600/60 bg-red-600/10 text-red-400 px-2 py-1 min-w-[70px]">
          <div className="text-[9px] tracking-[0.3em] uppercase leading-none">
            {dateStr.mon}
          </div>
          <div className="text-lg leading-none mt-0.5">{dateStr.day}</div>
        </div>
        <div className="min-w-0">
          <div className="font-display text-sm text-zinc-50 tracking-tight truncate">
            {gig.venue}
          </div>
          {location && (
            <div className="text-[10px] text-zinc-500 tracking-widest uppercase flex items-center gap-1 truncate">
              <MapPin className="w-2.5 h-2.5 shrink-0" strokeWidth={2.5} />
              {location}
            </div>
          )}
        </div>
      </div>
      {gig.ticket_url && (
        <a
          href={gig.ticket_url}
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 inline-flex items-center gap-1.5 border border-zinc-700 hover:border-red-600 bg-black hover:bg-red-600 px-2 py-1 text-[10px] tracking-widest uppercase font-display text-zinc-100 hover:text-white transition-colors"
          data-testid={`family-gig-tickets-${gig.id}`}
        >
          <Ticket className="w-3 h-3" strokeWidth={2.5} />
          TICKETS
        </a>
      )}
    </div>
  );
}

function formatGigDate(iso) {
  if (!iso) return { mon: "", day: "" };
  try {
    const [y, m, d] = iso.split("T")[0].split("-").map((n) => parseInt(n, 10));
    const dt = new Date(Date.UTC(y, m - 1, d));
    const mon = dt.toLocaleString("en-US", { month: "short", timeZone: "UTC" }).toUpperCase();
    return { mon, day: String(d).padStart(2, "0") };
  } catch {
    return { mon: "", day: "" };
  }
}
